// ─────────────────────────────────────────────────────────────────────────────
// Installing the app: what this browser can actually do about it.
//
// Three separate facts, and the card on the dashboard needs all three:
//
//   1. Is it already installed?  Then say nothing at all.
//   2. Can this browser be ASKED to install?  Then offer a real button.
//   3. If not, which browser is it?  Then give that browser's own steps.
//
// The third exists because `beforeinstallprompt` is a Chromium event. iOS Safari
// never fires it and never will — installing there is Share ▸ Add to Home Screen,
// a gesture only the person can perform. A button that calls a prompt which does
// not exist is worse than a sentence telling them where to tap, so the card
// shows the sentence and no button.
//
// ── CAPTURING THE EVENT ─────────────────────────────────────────────────────
//
// `beforeinstallprompt` fires once, early, and often before React has mounted.
// If nothing is listening at that moment the chance is gone for the life of the
// page. So the listener is installed at MODULE level, imported by `main.tsx`
// before anything renders, and the event is parked here for whoever asks later.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useState } from "react";

/** Chromium's install prompt event. Not in lib.dom, so it is declared here. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let parked: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function announce(): void {
  for (const listener of listeners) listener();
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    // Suppressing the default keeps Chrome's own mini-infobar away, so the
    // dashboard card is the single place the offer is made.
    event.preventDefault();
    parked = event as BeforeInstallPromptEvent;
    announce();
  });
  window.addEventListener("appinstalled", () => {
    parked = null;
    announce();
  });
}

/** How this browser installs an app, and therefore what the card should say. */
export type InstallRoute = "prompt" | "ios-safari" | "macos-safari" | "firefox-desktop" | "manual";

/**
 * Which set of instructions to show. Deliberately coarse: the point is to name
 * the right menu, not to identify the browser precisely.
 */
export function installRoute(ua: string, touchPoints: number, hasPrompt: boolean): InstallRoute {
  if (hasPrompt) return "prompt";

  // iPadOS reports a Mac user agent; the touch points give it away.
  const iOS = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && touchPoints > 1);
  const safari = /Safari/.test(ua) && !/Chrome|Chromium|Edg|OPR|FxiOS|CriOS/.test(ua);

  if (iOS) return "ios-safari";
  if (safari && /Macintosh/.test(ua)) return "macos-safari";
  if (/Firefox/.test(ua) && !/Android/.test(ua)) return "firefox-desktop";
  return "manual";
}

/** True when the page is running as an installed app rather than in a tab. */
export function isInstalled(): boolean {
  if (typeof window === "undefined") return false;
  // `navigator.standalone` is iOS Safari's own, non-standard answer, and the
  // only one it gives — home-screen apps there do not match display-mode.
  const iosStandalone = (window.navigator as { standalone?: boolean }).standalone === true;
  return window.matchMedia("(display-mode: standalone)").matches || iosStandalone;
}

export interface InstallState {
  readonly installed: boolean;
  readonly route: InstallRoute;
  /** Present only on the `prompt` route; shows the browser's own install dialog. */
  readonly promptInstall: (() => Promise<void>) | null;
}

export function useInstallState(): InstallState {
  const [installed, setInstalled] = useState(isInstalled);
  const [hasPrompt, setHasPrompt] = useState(() => parked !== null);

  useEffect(() => {
    const onChange = (): void => {
      setHasPrompt(parked !== null);
      setInstalled(isInstalled());
    };
    listeners.add(onChange);

    // Someone can install from the browser's own menu while this page is open;
    // the card should vanish then too, without a reload.
    const media = window.matchMedia("(display-mode: standalone)");
    media.addEventListener("change", onChange);
    // And run once on mount, in case the event fired between module load and here.
    onChange();

    return () => {
      listeners.delete(onChange);
      media.removeEventListener("change", onChange);
    };
  }, []);

  const promptInstall = useCallback(async (): Promise<void> => {
    const event = parked;
    if (event === null) return;
    await event.prompt();
    const choice = await event.userChoice;
    // The event is single-use whatever the answer: Chromium will fire a fresh
    // one on a later visit if the offer still stands.
    parked = null;
    if (choice.outcome === "accepted") setInstalled(true);
    announce();
  }, []);

  const route = installRoute(
    typeof navigator === "undefined" ? "" : navigator.userAgent,
    typeof navigator === "undefined" ? 0 : navigator.maxTouchPoints,
    hasPrompt,
  );

  return { installed, route, promptInstall: hasPrompt ? promptInstall : null };
}

/**
 * Turns the service worker on in production and off everywhere else.
 *
 * The `unregister` branch is not tidiness. A developer who once loaded a
 * production build on localhost has a worker registered for that origin, and it
 * will keep serving its cached shell over `npm run dev` until something removes
 * it — which presents as edits that do not appear, on a file that is already
 * correct.
 */
export function manageServiceWorker(isProduction: boolean): void {
  if (!("serviceWorker" in navigator)) return;

  if (!isProduction) {
    void navigator.serviceWorker.getRegistrations().then((registrations) => {
      for (const registration of registrations) void registration.unregister();
    });
    return;
  }

  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js").catch(() => {
      // An unavailable worker costs offline support and nothing else; the app
      // works from the network exactly as it did before.
    });
  });
}

/**
 * Whether this is a phone — a small screen with a finger on it.
 *
 * BOTH conditions, deliberately. Width alone calls a narrowed desktop window a
 * phone, and `pointer: coarse` alone calls a touchscreen laptop one. The pair
 * is what "somebody holding this" actually means.
 *
 * It exists so the install instructions appear where installing is a thing
 * people do and where the app is genuinely better installed — and not on the
 * desktop, where the same card was permanent furniture on the dashboard telling
 * an office computer to add Alba to its home screen.
 */
export function useIsPhone(): boolean {
  const [isPhone, setIsPhone] = useState(() => matchesPhone());

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const query = window.matchMedia(PHONE_QUERY);
    const onChange = (): void => { setIsPhone(matchesPhone()); };
    query.addEventListener("change", onChange);
    return () => { query.removeEventListener("change", onChange); };
  }, []);

  return isPhone;
}

const PHONE_QUERY = "(max-width: 820px) and (pointer: coarse)";

function matchesPhone(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(PHONE_QUERY).matches;
}
